import { gql, useMutation, useQuery } from '@apollo/client'
import flatten from 'lodash/flatten'
import { useMemo } from 'react'

import {
  ArtistCategoryQuery,
  ArtistCategoryQueryVariables,
  ConsolePostEditQuery,
  ConsolePostEditQueryVariables,
  MembershipInterval,
  PostBoxFragmentDoc,
  PostStatus,
  PostType,
  RepliesConnectionFragmentDoc,
  UpdatePostMutation,
  UpdatePostMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ postId: PostType }>()
  const { data } = useQuery<
    ConsolePostEditQuery,
    ConsolePostEditQueryVariables
  >(QUERY, {
    variables: { postId: router.query.postId },
  })

  const { data: artistPostCategory } = useQuery<
    ArtistCategoryQuery,
    ArtistCategoryQueryVariables
  >(POST_CATEGORY_QUERY)
  const [updatePost, { loading: loadingEditPost }] = useMutation<
    UpdatePostMutation,
    UpdatePostMutationVariables
  >(UPDATE_POST)

  const plans = useMemo(() => {
    if (data?.artist?.subscription?.products?.length) {
      return flatten(
        data?.artist?.subscription?.products.map(product => {
          const monthPlan = product.plans.find(
            plan => plan.interval === MembershipInterval.Month
          )
          if (monthPlan) {
            return [{ ...monthPlan, title: product.title }]
          }
          return product.plans.map(plan => ({ ...plan, title: product.title }))
        })
      )
    }
    if (data?.artist?.subscription?.plans?.length) {
      return data.artist.subscription.plans
    }
    return []
  }, [data?.artist?.subscription?.plans, data?.artist?.subscription?.products])

  const categories = useMemo(() => {
    if (artistPostCategory?.artist?.postCategory?.categories.length) {
      return artistPostCategory?.artist?.postCategory?.categories
    }
    return []
  }, [artistPostCategory?.artist?.postCategory?.categories])
  return {
    state: {
      artistId: user?.uid ?? '',
      categories: categories,
      post: data?.post,
      plans,
      loadingEditPost,
    },
    actions: {
      async onPostEdit(inputs) {
        let publishedAt = undefined
        if (data?.post?.status === PostStatus.Scheduled) {
          publishedAt = inputs.publishedAt ?? null
        }
        await updatePost({
          variables: {
            postId: router.query.postId,
            inputs: {
              ...inputs,
              publishedAt,
            },
          },
        })
        return
      },
    },
  }
})

const QUERY = gql`
  query ConsolePostEdit($postId: ID!, $pageInput: PageInput) {
    post(id: $postId) {
      ...PostBox
      replies(pageInput: $pageInput) {
        ...RepliesConnection
      }
    }
    artist {
      id
      subscription {
        plans {
          planId
          title
          interval
        }
        products {
          productId
          title
          plans {
            interval
            planId
          }
        }
      }
    }
    user {
      id
      accountId
      formattedAccountId @client
      displayName
      avatar {
        uri
        smallUri
      }
    }
  }
  ${RepliesConnectionFragmentDoc}
  ${PostBoxFragmentDoc}
`

const UPDATE_POST = gql`
  mutation UpdatePost($postId: ID!, $inputs: UpdatePostInput!) {
    updatePost(postId: $postId, inputs: $inputs) {
      ...PostBox
    }
  }
  ${PostBoxFragmentDoc}
`
const POST_CATEGORY_QUERY = gql`
  query artistCategory {
    artist {
      id
      postCategory {
        categories {
          id
          name
        }
      }
    }
  }
`
